"use client";

import { useState, useCallback } from "react";
import { Loader2, CheckCircle, FileCheck, ExternalLink } from "lucide-react";
import { routes } from "@/lib/routes";
interface TermsAcceptanceProps {
  dealId: string;
  isAccepted: boolean;
  acceptedAt?: string | null;
  onAccept: () => Promise<void>;
  disabled?: boolean;
  className?: string;
}

/**
 * TermsAcceptance - Component for accepting contract terms
 */
export function TermsAcceptance({
  isAccepted,
  acceptedAt,
  onAccept,
  disabled = false,
  className = ""
}: TermsAcceptanceProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [checked, setChecked] = useState(isAccepted);
  const handleAccept = useCallback(async () => {
    if (isAccepted || !checked) return;
    setIsSubmitting(true);
    try {
      await onAccept();
    } finally {
      setIsSubmitting(false);
    }
  }, [isAccepted, checked, onAccept]);
  if (isAccepted) {
    return <div className={`rounded-lg border border-emerald-200 bg-emerald-50 p-4 ${className}`}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-5 h-5 text-emerald-600" />
          </div>
          <div>
            <p className="font-medium text-emerald-700">Bedingungen akzeptiert</p>
            {acceptedAt && <p className="text-sm text-emerald-600">
                Akzeptiert am{" "}
                {new Date(acceptedAt).toLocaleDateString("de-DE", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit"
            })}
              </p>}
          </div>
        </div>
      </div>;
  }
  return <div className={`rounded-lg border border-zinc-200 bg-white p-4 ${className}`}>
      <div className="flex items-start gap-3 mb-4">
        <div className="w-10 h-10 rounded-full bg-zinc-100 flex items-center justify-center flex-shrink-0">
          <FileCheck className="w-5 h-5 text-zinc-600" />
        </div>
        <div>
          <p className="font-medium text-zinc-900">Vertragsbedingungen</p>
          <p className="text-sm text-zinc-500">
            Bitte lesen und akzeptieren Sie die Vertragsbedingungen
          </p>
        </div>
      </div>

      {/* Terms summary */}
      <div className="bg-zinc-50 rounded-lg p-4 mb-4 text-sm text-zinc-600 space-y-2">
        <p>Durch das Akzeptieren der Bedingungen bestätigen Sie:</p>
        <ul className="list-disc list-inside space-y-1 ml-2">
          <li>Die Angaben im Vertrag sind korrekt und vollständig</li>
          <li>Die Rechnungsadresse ist aktuell und zutreffend</li>
          <li>Sie sind berechtigt, diesen Vertrag abzuschließen</li>
          <li>Sie akzeptieren unsere AGB und Datenschutzbestimmungen</li>
        </ul>
        <a href={routes.content.agb} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-primary hover:text-primary/80 mt-2">
          AGB lesen <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {/* Checkbox */}
      <label className="flex items-start gap-3 cursor-pointer mb-4">
        <input type="checkbox" checked={checked} onChange={e => setChecked(e.target.checked)} disabled={disabled || isSubmitting} className="mt-1 w-5 h-5 rounded border-zinc-300 text-primary focus:ring-primary disabled:opacity-50" />
        <span className="text-sm text-zinc-700">
          Ich habe die Vertragsbedingungen gelesen und akzeptiere diese. Ich bestätige, dass alle
          Angaben korrekt sind.
        </span>
      </label>

      {/* Accept button */}
      <button onClick={handleAccept} disabled={disabled || isSubmitting || !checked} className="w-full px-4 py-2 bg-primary text-white font-medium rounded-lg hover:bg-primary/90 disabled:bg-zinc-300 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2">
        {isSubmitting ? <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Wird bestätigt...
          </> : <>
            <CheckCircle className="w-4 h-4" />
            Bedingungen akzeptieren
          </>}
      </button>
    </div>;
}